import type {Instant, UUID} from './common'
import type {EntityLink} from './entity-link'
import type {ResolverType, ApprovalStrategy} from './approval-policy'
import type {EscalationLevel} from './escalation'
export {RequestItemStatus, RequestStatus} from './request-base'
import type {RequestItemStatus, RequestStatus} from './request-base'
import {BadgeVariant} from '@/components/ui/badge'
import {EnumValue} from '@/components/shared/EnumValue'
/* eslint-disable i18next/no-literal-string -- const definitions are the source of truth */

/**
 * Access request types for frontend.
 */

export const ApprovalDecision = {
    APPROVED: 'APPROVED',
    REJECTED: 'REJECTED',
} as const;
export type ApprovalDecision = typeof ApprovalDecision[keyof typeof ApprovalDecision];

/**
 * Rich enum carrying UI rendering metadata for {@link ApprovalDecision}.
 * Callers that render approval-specific color accents use `.borderClass`
 * and `.textClass` instead of hardcoded Tailwind strings.
 */
export class ApprovalDecisionBadge extends EnumValue {
    static readonly APPROVED = new ApprovalDecisionBadge(
        ApprovalDecision.APPROVED,
        BadgeVariant.SUCCESS,
        (i18n) => i18n.approvalChain.approved as string,
        'border-green-600',
        'text-status-success',
    )
    static readonly REJECTED = new ApprovalDecisionBadge(
        ApprovalDecision.REJECTED,
        BadgeVariant.DESTRUCTIVE,
        (i18n) => i18n.approvalChain.rejected as string,
        'border-red-600',
        'text-status-danger',
    )

    static readonly values: ReadonlyArray<ApprovalDecisionBadge> = [
        ApprovalDecisionBadge.APPROVED,
        ApprovalDecisionBadge.REJECTED,
    ]

    static of(code: ApprovalDecision | string | null | undefined): ApprovalDecisionBadge | undefined {
        return ApprovalDecisionBadge.values.find((v) => v.code === code)
    }
}

export const ApprovalStepStatus = {
    PENDING: 'PENDING',
    ACTIVE: 'ACTIVE',
    APPROVED: 'APPROVED',
    REJECTED: 'REJECTED',
    SKIPPED: 'SKIPPED',
} as const;
export type ApprovalStepStatus = typeof ApprovalStepStatus[keyof typeof ApprovalStepStatus];

/**
 * Approver assigned to an item or step.
 */
export interface AccessRequestItemApprover {
    id: UUID;
    approver: EntityLink;
    assignedAt: Instant;
}

/**
 * Approval decision record.
 */
export interface AccessRequestApproval {
    id: UUID;
    approver: EntityLink;
    decision: ApprovalDecision;
    comment: string | null;
    decidedAt: Instant;
}

/**
 * A single step in the approval chain.
 */
export interface ApprovalStep {
    id: UUID;
    stepOrder: number;
    resolverType: ResolverType;
    resolverParam: string | null;
    approvalStrategy: ApprovalStrategy;
    required: boolean;
    status: ApprovalStepStatus;
    activatedAt: Instant | null;
    completedAt: Instant | null;
    approvers: AccessRequestItemApprover[];
    decisions: AccessRequestApprovalResponse[];
    escalationLevel?: EscalationLevel | null;
    lastEscalationAt?: Instant | null;
    nextDeadlineAt?: Instant | null;
    platformOwnerName?: string | null;
}

/**
 * Approval decision within a step.
 */
export interface AccessRequestApprovalResponse {
    id: UUID;
    approver: EntityLink;
    decision: string;
    comment: string | null;
    decidedAt: Instant;
}

/**
 * Access request item response from the API.
 */
export interface AccessRequestItem {
    id: UUID;
    requestId: UUID;
    accessProfile: EntityLink;
    resource: EntityLink;
    riskLevel: string;
    status: RequestItemStatus;
    approvalStrategy: ApprovalStrategy;
    matchedPolicyName: string;
    expiresAt: Instant | null;
    approvedAt: Instant | null;
    fulfilledAt: Instant | null;
    rejectedAt: Instant | null;
    cancelledAt: Instant | null;
    cancelReason: string | null;
    requester: EntityLink;
    beneficiary: EntityLink;
    justification: string;
    approvalChain: ApprovalStep[];
    assignedApprovers: AccessRequestItemApprover[];
    approvals: AccessRequestApproval[];
    version: number;
    createdAt: Instant;
}

/**
 * Access request response from the API.
 */
export interface AccessRequest {
    id: UUID;
    requester: EntityLink;
    beneficiary: EntityLink;
    justification: string;
    status: RequestStatus;
    items: AccessRequestItem[];
    version: number;
    createdAt: Instant;
    updatedAt: Instant;
}

/**
 * Item within a create request.
 */
export interface AccessRequestItemCreate {
    accessProfileId: UUID;
    expiresAt?: Instant;
}

/**
 * Request to create a new access request.
 */
export interface AccessRequestCreateRequest {
    beneficiaryIdentityId: UUID;
    justification: string;
    items: AccessRequestItemCreate[];
}

/**
 * Bulk request — N beneficiaries, one cartesian item set, all-or-nothing.
 * Spec: docs/specs/access-request-multi-recipient/architecture.md §5.
 * Wired into services/real and the wizard in 674.03.
 */
export interface AccessRequestBulkCreateRequest {
    beneficiaryIdentityIds: UUID[];
    justification: string;
    items: AccessRequestItemCreate[];
}

/**
 * Per-row summary of one of the N requests created by a bulk submission.
 */
export interface BulkAccessRequestSummary {
    id: UUID;
    beneficiary: EntityLink;
    itemCount: number;
    status: RequestStatus;
}

/**
 * 201 response for the bulk endpoint.
 */
export interface BulkAccessRequest {
    createdRequests: BulkAccessRequestSummary[];
}

/**
 * Wire shape for one entry under the AR0019 bulk-validation error envelope's
 * `params.perBeneficiaryErrors`. Mirrors the backend record
 * `BulkValidationException.BeneficiaryErrorEntry` (Java).
 */
export interface BulkValidationErrorRecord {
    code: string;
    message: string;
}

export interface BulkValidationBeneficiaryErrorEntry {
    beneficiaryIdentityId: UUID;
    errors: BulkValidationErrorRecord[];
}

export interface BulkValidationErrorParams {
    perBeneficiaryErrors: BulkValidationBeneficiaryErrorEntry[];
}

/**
 * Request to approve an item.
 */
export interface ApproveItemRequest {
    comment?: string;
}

/**
 * Request to reject an item.
 */
export interface RejectItemRequest {
    comment: string;
}

/**
 * Request to cancel an item or request.
 */
export interface CancelItemRequest {
    reason: string;
}

/**
 * Paginated response for access requests.
 */
export interface AccessRequestPage {
    content: AccessRequest[];
    totalElements: number;
    totalPages: number;
    number: number;
    size: number;
}

/**
 * Paginated response for access request items (for approver view).
 */
export interface AccessRequestItemPage {
    content: AccessRequestItem[];
    totalElements: number;
    totalPages: number;
    number: number;
    size: number;
}

/**
 * Peer access profile recommendation from the API.
 */
export interface PeerAccessProfile {
    accessProfile: EntityLink;
    resource: EntityLink;
    environment: string;
    expiryRequired: boolean;
    maxDurationDays: number | null;
    peerCount: number;
}

/**
 * Batch approve request.
 */
export interface BatchApproveRequest {
    itemIds: UUID[];
    comment?: string;
}

/**
 * Batch reject request.
 */
export interface BatchRejectRequest {
    itemIds: UUID[];
    comment: string;
}

/* eslint-disable i18next/no-literal-string -- field name constants for backend API */
export const AccessRequestApprovalResponseFields = {
    id: 'id',
    approver: 'approver',
    decision: 'decision',
    comment: 'comment',
    decidedAt: 'decidedAt',
} as const

export const AccessRequestCreateRequestFields = {
    beneficiaryIdentityId: 'beneficiaryIdentityId',
    justification: 'justification',
    items: 'items',
} as const

export const AccessRequestItemApproverResponseFields = {
    id: 'id',
    approver: 'approver',
    assignedAt: 'assignedAt',
} as const

export const AccessRequestItemResponseFields = {
    id: 'id',
    requestId: 'requestId',
    accessProfile: 'accessProfile',
    resource: 'resource',
    riskLevel: 'riskLevel',
    status: 'status',
    approvalStrategy: 'approvalStrategy',
    matchedPolicyName: 'matchedPolicyName',
    expiresAt: 'expiresAt',
    approvedAt: 'approvedAt',
    fulfilledAt: 'fulfilledAt',
    rejectedAt: 'rejectedAt',
    cancelledAt: 'cancelledAt',
    cancelReason: 'cancelReason',
    requester: 'requester',
    beneficiary: 'beneficiary',
    justification: 'justification',
    approvalChain: 'approvalChain',
    assignedApprovers: 'assignedApprovers',
    approvals: 'approvals',
    version: 'version',
    createdAt: 'createdAt',
} as const

/** Shared field names for governance request responses (access requests, function group requests). */
export const GovernanceRequestFields = {
    id: 'id',
    requester: 'requester',
    beneficiary: 'beneficiary',
    justification: 'justification',
    status: 'status',
    items: 'items',
    version: 'version',
    createdAt: 'createdAt',
    updatedAt: 'updatedAt',
} as const

export const AccessRequestResponseFields = {
    id: 'id',
    requester: 'requester',
    beneficiary: 'beneficiary',
    justification: 'justification',
    status: 'status',
    items: 'items',
    version: 'version',
    createdAt: 'createdAt',
    updatedAt: 'updatedAt',
} as const

export const ApprovalStepResponseFields = {
    id: 'id',
    stepOrder: 'stepOrder',
    resolverType: 'resolverType',
    resolverParam: 'resolverParam',
    approvalStrategy: 'approvalStrategy',
    required: 'required',
    status: 'status',
    activatedAt: 'activatedAt',
    completedAt: 'completedAt',
    approvers: 'approvers',
    decisions: 'decisions',
    escalationLevel: 'escalationLevel',
    lastEscalationAt: 'lastEscalationAt',
    nextDeadlineAt: 'nextDeadlineAt',
    platformOwnerName: 'platformOwnerName',
} as const

export const ApproveItemRequestFields = {
    comment: 'comment',
} as const

export const BatchApproveRequestFields = {
    itemIds: 'itemIds',
    comment: 'comment',
} as const

export const BatchRejectRequestFields = {
    itemIds: 'itemIds',
    comment: 'comment',
} as const

export const CancelItemRequestFields = {
    reason: 'reason',
} as const

export const PeerAccessProfileResponseFields = {
    accessProfile: 'accessProfile',
    resource: 'resource',
    environment: 'environment',
    expiryRequired: 'expiryRequired',
    maxDurationDays: 'maxDurationDays',
    peerCount: 'peerCount',
} as const

export const RejectItemRequestFields = {
    comment: 'comment',
} as const
/* eslint-enable i18next/no-literal-string */
